'use client';

import { useState, useEffect } from 'react';
import { Box, TextField, CircularProgress, Stack, MenuItem, Grid, Switch, FormControlLabel, Button, InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';

import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { createDriverLicense, updateDriverLicense, getDriverLicenseById } from '../../../stores/driverLicenseStore';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const MasterDriverLicenseAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const [initialValues, setInitialValues] = useState({
    company: '',
    company_name: '',
    license_name: '',
    is_lifetime: false,
    periode_notification: '',
    threshold: null,
    threshold_danger: null,
    value_periode_notification: null
  });

  const [loading, setLoading] = useState(false);
  const notify = useNotifier();
  const { t } = useLanguage();
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const companyOptions = companyPickerParams.options;

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    const fetchData = async () => {
      if (!id) {
        setInitialValues({
          company: '',
          company_name: '',
          license_name: '',
          is_lifetime: false,
          periode_notification: 'day',
          threshold: '',
          threshold_danger: '',
          value_periode_notification: ''
        });
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getDriverLicenseById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues({
            company: data.company != null ? String(data.company) : '',
            company_name: data.company_name ?? data.join?.company_name ?? '',
            license_name: data.license_name ?? '',
            is_lifetime: data.is_lifetime ?? false,
            periode_notification: data.periode_notification ?? 'day',
            threshold: data.threshold ?? 0,
            threshold_danger: data.threshold_danger ?? 0,
            value_periode_notification: data.value_periode_notification ?? 0
          });
        }
      } catch (err) {
        console.error(err);
        notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  useEffect(() => {
    if (id || !open) return;

    let storedCompany = null;
    try {
      const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed) {
          storedCompany = parsed.company ?? parsed.company_id ?? null;
        }
      }
    } catch (e) {
      console.log(e);
    }

    if (storedCompany == null) {
      try {
        const rawCompany = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
        if (rawCompany && String(rawCompany) !== 'all') storedCompany = rawCompany;
      } catch (e) {
        console.log(e);
      }
    }

    if (storedCompany == null) return;

    const stored = String(storedCompany);
    const match = companyOptions.find((c) => String(c.value) === stored);

    if (match) {
      setInitialValues((prev) => ({
        ...prev,
        company: String(match.value),
        company_name: match.label ?? match.name ?? String(match.value)
      }));
    } else {
      setInitialValues((prev) => ({
        ...prev,
        company: stored,
        company_name: stored
      }));
    }
  }, [companyOptions, id, open]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('validation.companyRequired', 'Company is required')),
    license_name: Yup.string().required(t('validation.licenseNameRequired', 'License Name is required')),
    is_lifetime: Yup.boolean(),
    periode_notification: Yup.string().when('is_lifetime', {
      is: false,
      then: (schema) => schema.required(t('validation.periodeRequired', 'Periode is required'))
    }),
    threshold: Yup.number()
      .nullable()
      .when('is_lifetime', {
        is: false,
        then: (schema) => schema.required(t('validation.thresholdWarningRequired', 'Threshold Warning is required')).min(0)
      }),
    threshold_danger: Yup.number()
      .nullable()
      .when('is_lifetime', {
        is: false,
        then: (schema) =>
          schema
            .required('Threshold Danger is mandatory')
            .min(0)
            .test(
              'danger-less-than-warning',
              t('validation.dangerLessThanWarning', 'Threshold Danger must be smaller than Threshold Warning'),
              function (value) {
                const { threshold } = this.parent;
                return value < threshold;
              }
            )
      })
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const isLife = Boolean(values.is_lifetime);
        const payload = {
          ...values,
          license_name: formatUpperCase(values.license_name),
          id: id ?? 0,
          company: Number(values.company),
          is_lifetime: isLife,
          threshold: isLife ? 0 : Number(values.threshold || 0),
          threshold_danger: isLife ? 0 : Number(values.threshold_danger || 0),
          value_periode_notification: isLife ? 0 : Number(values.value_periode_notification || 0),
          periode_notification: isLife ? '' : values.periode_notification
        };
        let response;
        if (id) {
          response = await updateDriverLicense(id, payload);
        } else {
          response = await createDriverLicense(payload);
        }
        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }

        notify({
          message: id ? t('notify.update', 'Updated successfully') : t('notify.create', 'Created successfully'),
          severity: 'success'
        });
        resetForm();

        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleCustomSubmit = async () => {
    const errors = await formik.validateForm();
    const errorKeys = Object.keys(errors);

    if (errorKeys.length > 0) {
      const firstErrorKey = errorKeys[0];
      const errorMessage = errors[firstErrorKey];
      let displayMessage = errorMessage;

      // Jika errornya dari test() custom Yup (seperti danger-less-than-warning)
      if (firstErrorKey === 'threshold_danger' && formik.values.threshold_danger >= formik.values.threshold) {
        displayMessage = t('validation.thresholdDanger', 'Threshold Danger must be smaller than Warning!');
      } else if (typeof errorMessage === 'object') {
        displayMessage = Object.values(errorMessage)[0] || 'Invalid data';
      }

      notify({ message: displayMessage, severity: 'warning' });

      formik.setTouched(errorKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      return;
    }

    // Jika validasi Formik (termasuk cek threshold_danger < threshold) aman, cek manual sekali lagi sebagai pengaman ekstra
    if (!formik.values.is_lifetime) {
      const danger = Number(formik.values.threshold_danger || 0);
      const warning = Number(formik.values.threshold || 0);
      if (danger >= warning) {
        notify({
          message: t('validation.thresholdDanger', 'Threshold Danger must be smaller than Warning!'),
          severity: 'warning'
        });
        formik.setFieldTouched('threshold_danger', true);
        formik.setFieldError('threshold_danger', t('validation.thresholdDanger', 'Threshold Danger must be smaller than Warning!'));
        return;
      }
    }

    formik.handleSubmit();
  };

  const dialogActions = (
    <>
      <Button onClick={onClose} disabled={formik.isSubmitting} sx={{ textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleCustomSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={id ? t('driverLicense.edit', 'Edit Master Driver License') : t('driverLicense.add', 'Add Master Driver License')}
      maxWidth="sm"
      fullHeight={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', mt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* Kolom 1 */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <Stack spacing={2}>
                  <PickerField
                    label={t('general.company', 'Company') + ' *'}
                    value={formik.values.company}
                    {...companyPickerParams}
                    options={companyOptions}
                    onChange={(opt) => {
                      const val = typeof opt === 'object' ? opt?.value : opt;
                      const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
                      const selectedId = selected ? selected.value : (val ?? null);
                      formik.setFieldValue('company', selectedId);
                      if (selected) formik.setFieldValue('company_name', selected.label);
                    }}
                    dialogTitle={t('general.selectCompany', 'Select Company')}
                    error={formik.touched.company && !!formik.errors.company}
                    helperText={formik.touched.company && formik.errors.company}
                  />
                  <TextField
                    label={`${t('driverLicense.licenseName', 'License Name')} *`}
                    name="license_name"
                    value={formik.values.license_name}
                    onChange={(e) => {
                      formik.setFieldValue('license_name', formatUpperCase(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    fullWidth
                    error={formik.touched.license_name && Boolean(formik.errors.license_name)}
                    helperText={formik.touched.license_name && formik.errors.license_name}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.is_lifetime}
                        onChange={(e) => formik.setFieldValue('is_lifetime', e.target.checked)}
                        color="primary"
                        name="is_lifetime"
                      />
                    }
                    label={t('driverLicense.isLifetime', 'Is Lifetime')}
                  />
                </Stack>
              </Grid>

              {/* Kolom 2 & 3 (Hanya tampil jika NOT lifetime) */}
              {!formik.values.is_lifetime && (
                <>
                  <Grid item size={{ xs: 12, md: 4 }}>
                    <Stack spacing={2}>
                      <TextField
                        label={t('driverLicense.thresholdWarning', 'Threshold Warning')}
                        name="threshold"
                        type="number"
                        value={formik.values.threshold ?? ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                        error={formik.touched.threshold && Boolean(formik.errors.threshold)}
                        helperText={formik.touched.threshold && formik.errors.threshold}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{t('general.days', 'Days')}</InputAdornment>
                        }}
                      />
                      <TextField
                        label={`${t('vehicleLicense.frequencyNotif', 'Frequency Notif')} *`}
                        name="value_periode_notification"
                        type="number"
                        value={formik.values.value_periode_notification ?? ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                        error={formik.touched.value_periode_notification && Boolean(formik.errors.value_periode_notification)}
                        helperText={formik.touched.value_periode_notification && formik.errors.value_periode_notification}
                      />
                    </Stack>
                  </Grid>

                  <Grid item size={{ xs: 12, md: 4 }}>
                    <Stack spacing={2}>
                      <TextField
                        label={t('driverLicense.thresholdDanger', 'Threshold Danger')}
                        name="threshold_danger"
                        type="number"
                        value={formik.values.threshold_danger ?? ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                        error={formik.touched.threshold_danger && Boolean(formik.errors.threshold_danger)}
                        helperText={formik.touched.threshold_danger && formik.errors.threshold_danger}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{t('general.days', 'Days')}</InputAdornment>
                        }}
                      />
                      <TextField
                        select
                        label={`${t('driverLicense.notifPeriode', 'Notif Periode')} *`}
                        name="periode_notification"
                        value={formik.values.periode_notification}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                        error={formik.touched.periode_notification && Boolean(formik.errors.periode_notification)}
                        helperText={formik.touched.periode_notification && formik.errors.periode_notification}
                      >
                        <MenuItem value="day">{t('general.day', 'Day')}</MenuItem>
                        <MenuItem value="week">{t('general.week', 'Week')}</MenuItem>
                        <MenuItem value="month">{t('general.month', 'Month')}</MenuItem>
                      </TextField>
                    </Stack>
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterDriverLicenseAddEdit;
